import { useCalendarConnectionIndex } from '@jobilla/nexus-sdk'
import { Link } from '@tanstack/react-router'
import { AlertTriangleIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

export interface ReconnectCalendarBannerProps {
	linkToSettings?: boolean
}

/**
 * Persistent "reconnect your calendar" banner. Self-fetches the user's calendar
 * connection and renders nothing unless it needs reconnecting, so it can be
 * dropped into any recruiter surface — the availability/calendar pages, the
 * interviews tab, the send-booking-link dialog — without per-surface gating.
 *
 * `linkToSettings` is off on the calendar settings page itself, where the
 * reconnect action lives on the provider card — a link back to the current page
 * would be circular.
 */
export function ReconnectCalendarBanner({ linkToSettings = true }: ReconnectCalendarBannerProps) {
	const { t } = useTranslation('common')
	const { data } = useCalendarConnectionIndex()
	const needsReconnect = data?.some((connection) => connection.needsReconnect) ?? false

	// No calendar surface while the integration is gated off — nowhere to reconnect.
	if (!needsReconnect) {
		return null
	}

	return (
		<div role="alert" className="flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-800">
			<AlertTriangleIcon className="mt-0.5 size-4 shrink-0" />
			<div className="flex-1">
				{t('account.calendar.reconnect_banner.message')}
				{linkToSettings
					? (
						<>
							{' '}
							<Link to="/account/calendar" className="font-medium underline">
								{t('account.calendar.reconnect_banner.action')}
							</Link>
						</>
					)
					: null}
			</div>
		</div>
	)
}
